import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  Grid2,
  Stack,
  Typography,
} from '@mui/material';
import type { SxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormikContext } from 'formik';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { FormikDropdownField, FormikTextField } from '@components/FormikFields';
import Icon from '@components/Icon/Icon';
import List from '@components/List';

import vitallLogo from '@images/logos/favicon.png';

import { Contact } from '@models/contact';
import { PractitionerRecord } from '@models/practitioner';
import type { FormValues } from '@models/related-person';

import { useListPractitionersLazyQuery } from 'generated/practitioner.generated';

const messages = defineMessages({
  address: {
    id: 'GLOBAL.address',
    defaultMessage: 'Address',
  },
  postalCode: {
    id: 'GLOBAL.postalCode',
    defaultMessage: 'Postal/Zip Code',
  },
  phone: {
    id: 'GLOBAL.phone',
    defaultMessage: 'Phone Number',
  },
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email',
  },
  name: {
    id: 'contactForm.practitionerSearch.name',
    defaultMessage: 'First/Last Name',
  },
  manyAddresses: {
    id: 'contactForm.practitionerSearch.address.many',
    defaultMessage: '{count} addresses',
  },
  noAddresses: {
    id: 'contactForm.practitionerSearch.address.none',
    defaultMessage: 'No addresses',
  },
  select: {
    id: 'GLOBAL.select',
    defaultMessage: 'Select',
  },
  activePractitioners: {
    id: 'contactForm.practitionerSearch.listHeader.activePractitioners',
    defaultMessage: 'Active FHIR Practitioners ({count})',
  },
  inactivePractitioners: {
    id: 'contactForm.practitionerSearch.listHeader.inactivePractitioners',
    defaultMessage: 'Inactive FHIR Practitioners ({count})',
  },
  vitallPractitionerUsers: {
    id: 'contactForm.practitionerSearch.listHeader.vitallPractitionerUsers',
    defaultMessage: 'VITALL Practitioners ({count})',
  },
  queryResult: {
    id: 'contactForm.practitionerSearch.queryResult',
    defaultMessage:
      'There are <b>{count}</b> matching practitioners with the {filter} of <b>{text}</b>.',
  },
  noMatch: {
    id: 'contactForm.practitionerSearch.noMatch',
    defaultMessage: 'No exact match? Create a new practitioner instead',
  },
  stepOne: {
    id: 'contactForm.practitionerSearch.stepOne',
    defaultMessage: 'Step 1: Search for existing practitioners',
  },
  stepTwo: {
    id: 'contactForm.practitionerSearch.stepTwo',
    defaultMessage: 'Step 2: Add new practitioner to FHIR server and care team',
  },
  searchBy: {
    id: 'contactForm.practitionerSearch.searchBy',
    defaultMessage: 'Search by',
  },
  searchValue: {
    id: 'contactForm.practitionerSearch.searchValue',
    defaultMessage: 'Search value',
  },
  search: {
    id: 'contactForm.practitionerSearch.search',
    defaultMessage: 'Search',
  },
  createNew: {
    id: 'contactForm.practitionerSearch.createNew',
    defaultMessage: 'Create New',
  },
  selected: {
    id: 'contactForm.practitionerSearch.selected',
    defaultMessage: 'Selected',
  },
});

const sectionMessageMap: Record<
  string,
  (typeof messages)[keyof typeof messages]
> = {
  vitallPractitionerUsers: messages.vitallPractitionerUsers,
  activePractitioners: messages.activePractitioners,
  inactivePractitioners: messages.inactivePractitioners,
};

const filterMessageMap: Record<
  string,
  (typeof messages)[keyof typeof messages]
> = {
  address: messages.address,
  email: messages.email,
  name: messages.name,
  phone: messages.phone,
  postal_code: messages.postalCode,
};

/**
 * The contact fields populated from a selected practitioner. Kept as a single
 * source of truth so the populate (handleClick) and reset paths can't drift and
 * leak one practitioner's PII onto another record (VEG-677).
 */
const CLEARED_CONTACT_FIELDS = {
  contact_id: null,
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address: '',
  address2: '',
  country: '',
  region: '',
  city: '',
  postalCode: '',
} satisfies Partial<FormValues>;

export default function FhirPractitionerSearch() {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { setFieldTouched, setFieldValue, setValues, validateForm, values } =
    useFormikContext<FormValues>();
  const [baseLoading, setBaseLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [existingPractitioners, setExistingPractitioners] = useState<
    PractitionerRecord[]
  >([]);

  const [searchPractitioners, { data, loading: queryLoading }] =
    useListPractitionersLazyQuery();
  const loading = baseLoading || queryLoading;

  const prevSearchTextRef = useRef(values.search?.text);
  const prevSearchFilterRef = useRef(values.search?.filter);

  /**
   * Clears every contact field populated from a selected practitioner and
   * applies the given search patch in a SINGLE setValues. Both must happen in
   * one update: two separate Formik dispatches in the same tick read the same
   * stale state, so one silently clobbers the other — which previously let a
   * selected practitioner's PII survive a reset onto another record (VEG-677).
   */
  const resetContact = useCallback(
    (searchPatch: Partial<NonNullable<FormValues['search']>>) => {
      setValues((prev) => ({
        ...prev,
        ...CLEARED_CONTACT_FIELDS,
        search: { ...prev.search, ...searchPatch } as FormValues['search'],
      }));
    },
    [setValues]
  );

  /** When 'search.text' or 'search.filter' changes after a query has been made,
   * 'has_queried' is set back to false
   *  'no_match' is set back to null
   *  'existingPractitioners' is cleared
   *  Previously selected practitioner fields are reset
   */
  useEffect(() => {
    const searchText = values.search?.text;
    const searchFilter = values.search?.filter;
    if (
      (prevSearchTextRef.current !== searchText ||
        prevSearchFilterRef.current !== searchFilter) &&
      values.search?.has_queried
    ) {
      resetContact({ has_queried: false, no_match: null });
      setExistingPractitioners([]);
    }
    prevSearchTextRef.current = searchText;
    prevSearchFilterRef.current = searchFilter;
  }, [
    values.search?.text,
    values.search?.filter,
    values.search?.has_queried,
    resetContact,
  ]);

  const handlePractitionerQuery = async () => {
    const { search } = values;
    if (!search?.text || !search.filter) {
      setExistingPractitioners([]);
      return;
    }

    setBaseLoading(true);
    setSearchError(null);

    try {
      const errors = await validateForm();
      const searchErrors = errors.search as FormValues['search'];
      if (searchErrors?.text) {
        setBaseLoading(false);
        return;
      }
    } catch {
      setBaseLoading(false);
      return;
    }

    try {
      const { data: queryData, error } = await searchPractitioners({
        variables: { input: { [search.filter as string]: search.text } },
      });

      if (error) {
        setSearchError(error.message);
      }

      if (queryData && !error) {
        setFieldValue('search.has_queried', true);

        const practitionerRecords = queryData.listPractitioners.map(
          (p) => new PractitionerRecord(p)
        );
        setExistingPractitioners(practitionerRecords);
      }
    } catch (err) {
      setSearchError(
        err instanceof Error ? err.message : 'An unexpected error occurred'
      );
    } finally {
      setBaseLoading(false);
    }
  };

  const practitionerSections = useMemo(() => {
    return existingPractitioners.reduce<Record<string, PractitionerRecord[]>>(
      (acc, curr) => {
        const isVitallUser =
          Contact.isPractitioner(curr.model) && curr.model.is_vitall_user;
        if (isVitallUser) {
          acc['vitallPractitionerUsers'] = [
            ...(acc['vitallPractitionerUsers'] ?? []),
            curr,
          ];
        } else if (curr.isActive) {
          acc['activePractitioners'] = [
            ...(acc['activePractitioners'] ?? []),
            curr,
          ];
        } else {
          acc['inactivePractitioners'] = [
            ...(acc['inactivePractitioners'] ?? []),
            curr,
          ];
        }
        return acc;
      },
      {
        vitallPractitionerUsers: [],
        activePractitioners: [],
        inactivePractitioners: [],
      }
    );
  }, [existingPractitioners]);

  const searchFieldOptions = useMemo(() => {
    return [
      {
        k: 'address',
        v: formatMessage(messages.address),
      },
      {
        k: 'email',
        v: formatMessage(messages.email),
      },
      {
        k: 'name',
        v: formatMessage(messages.name),
      },
      {
        k: 'phone',
        v: formatMessage(messages.phone),
      },
      {
        k: 'postal_code',
        v: formatMessage(messages.postalCode),
      },
    ];
  }, [formatMessage]);

  return (
    <>
      {/* 'Step 1: Search for existing practitioners' */}
      <Grid2 size={{ xs: 12 }}>
        <Stack direction="row">
          {values.search?.no_match && <Icon name="lock" type="fad" />}
          <Typography>{formatMessage(messages.stepOne)}</Typography>
        </Stack>
      </Grid2>

      {/* Filters and search button for practitioner query*/}
      <Grid2 size={{ xs: 12, sm: 3 }}>
        <FormikDropdownField
          disabled={values.search?.no_match ?? false}
          label={formatMessage(messages.searchBy)}
          name="search.filter"
          onChange={(value) => setFieldValue('search.filter', value)}
          options={searchFieldOptions}
          required
        />
      </Grid2>
      <Grid2 size={{ xs: 10, sm: 7 }}>
        <FormikTextField
          disabled={values.search?.no_match ?? false}
          endAdornment={
            loading ? <CircularProgress color="inherit" size={20} /> : null
          }
          label={formatMessage(messages.searchValue)}
          name="search.text"
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              setFieldTouched('search.text', true);
              handlePractitionerQuery();
            }
          }}
          required
        />
      </Grid2>
      <Grid2
        size={{ xs: 2 }}
        sx={{ display: 'flex', alignItems: 'flex-end', pb: 1.5 }}
      >
        <Button
          disabled={!(values.search?.text && values.search.filter) || loading}
          onClick={handlePractitionerQuery}
        >
          {formatMessage(messages.search)}
        </Button>
      </Grid2>

      {searchError && (
        <Grid2 size={{ xs: 12 }}>
          <Typography color="error" variant="caption">
            {searchError}
          </Typography>
        </Grid2>
      )}

      {/* 'There are x matching practitioners with the filter of y.' */}
      {data?.listPractitioners && !loading && values.search?.has_queried && (
        <Grid2 size={{ xs: 12 }}>
          <Typography variant="caption">
            {formatMessage(messages.queryResult, {
              count: existingPractitioners.length,
              filter: formatMessage(
                values.search.filter
                  ? filterMessageMap[values.search.filter]
                  : messages.name
              ).toLowerCase(),
              text: values.search.text,
              b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
            })}
          </Typography>
        </Grid2>
      )}

      {/* List of practitioners matching the filters */}
      {existingPractitioners.length > 0 && (
        <Grid2 size={{ xs: 12 }}>
          <List
            buttonProps={{
              py: 0,
            }}
            type="existing-fhir-practitioner"
            maxHeight={200}
            sections={Object.keys(practitionerSections)
              .filter((k) => practitionerSections[k].length > 0)
              .map((k) => {
                return {
                  header: formatMessage(sectionMessageMap[k], {
                    count: practitionerSections[k].length,
                  }),
                  items: [...practitionerSections[k]]
                    .sort(({ name: nameA }, { name: nameB }) =>
                      (nameA ?? '').localeCompare(nameB ?? '')
                    )
                    .map((option) => {
                      const {
                        address, // address & address2 are primary address
                        address2,
                        city,
                        country,
                        email,
                        first_name,
                        id,
                        isActive,
                        last_name,
                        model,
                        name,
                        phone,
                        postalCode,
                        primaryAddress,
                        region,
                      } = option;
                      const isSelected = id === values.contact_id;

                      const formattedAddress = !model.address
                        ? formatMessage(messages.noAddresses)
                        : model.address.length > 1
                          ? formatMessage(messages.manyAddresses, {
                              count: model.address.length,
                            })
                          : primaryAddress;
                      const isVitallUser =
                        Contact.isPractitioner(model) && model.is_vitall_user;

                      const baseAvatarProps: SxProps = {
                        width: 25,
                        height: 25,
                      };

                      return {
                        disabled: values.search?.no_match ? true : !isActive,
                        title: (
                          <Stack component="span" direction="row">
                            <Typography component="span">{name}</Typography>
                            <Typography component="span" variant="caption">
                              {formattedAddress}
                            </Typography>
                          </Stack>
                        ),
                        subtitle:
                          email || phone ? (
                            <Typography component="span" variant="caption">
                              {[email, phone].filter(Boolean).join(' • ')}
                            </Typography>
                          ) : undefined,
                        startingAction: (
                          <Badge
                            overlap="circular"
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: isActive
                                  ? theme.palette.success.main
                                  : theme.palette.grey[700],
                              },
                              mr: 0.5,
                            }}
                            variant="dot"
                          >
                            {isVitallUser ? (
                              <Avatar
                                sx={{
                                  ...baseAvatarProps,
                                  border: `1px solid ${theme.palette.secondary.main}`,
                                }}
                                src={vitallLogo}
                              />
                            ) : (
                              <Avatar
                                sx={{
                                  ...baseAvatarProps,
                                  backgroundColor: 'transparent',
                                  border: `1px solid ${theme.palette.primary.light}`,
                                }}
                              >
                                <Icon
                                  color="primary.light"
                                  name="user-doctor"
                                  size="lg"
                                  type="fas"
                                />
                              </Avatar>
                            )}
                          </Badge>
                        ),
                        endingAction: (
                          <Chip
                            color={isSelected ? 'secondary' : 'primary'}
                            label={formatMessage(
                              isSelected ? messages.selected : messages.select
                            )}
                            size="small"
                            sx={{ ml: 'auto', textTransform: 'uppercase' }}
                            variant="outlined"
                          />
                        ),
                        handleClick: () => {
                          setValues((prev) => ({
                            ...prev,
                            contact_id: id,
                            first_name: first_name ?? '',
                            last_name: last_name ?? '',
                            email: email ?? '',
                            phone: phone ?? '',
                            address: address ?? '',
                            address2: address2 ?? '',
                            country: country ?? '',
                            region: region ?? '',
                            city: city ?? '',
                            postalCode: postalCode ?? '',
                          }));
                        },
                      };
                    }),
                };
              })}
          />
        </Grid2>
      )}

      {/* If there are no results, prompt user to add a new practitioner */}
      {data?.listPractitioners && !loading && values.search?.has_queried && (
        <Grid2 size={{ xs: 12 }}>
          <Card>
            <CardHeader
              action={
                <Button
                  disabled={Boolean(values.search.no_match)}
                  onClick={() => resetContact({ no_match: true })}
                >
                  {formatMessage(messages.createNew)}
                </Button>
              }
              avatar={<Icon color="primary.main" name="user-plus" type="fad" />}
              title={<Typography>{formatMessage(messages.noMatch)}</Typography>}
            />
          </Card>
          {values.search.no_match && (
            <Typography my={1}>{formatMessage(messages.stepTwo)}</Typography>
          )}
        </Grid2>
      )}
    </>
  );
}
